import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import dayjs from 'dayjs';

import { CircularProgress, Accordion, AccordionSummary, AccordionDetails, Typography, IconButton, Box, Button, TextField, Switch, Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Update, ExpandMore } from '@mui/icons-material';

import useErrorManager from "../../../hooks/useErrorManager";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

import GenericDetailsTable from '../../Layout/Table/GenericDetailsTable';
import TextLinkButton from "../../Layout/TextLinkButton"
import GenericDatePicker from '../../Layout/GenericDatePicker';

import { useNotification } from '../../../contexts/NotificationContext';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext'; // Import breadcrumb context


const EditableTextField = styled(TextField)`
    & .MuiInputBase-input {
        padding: 8px 8px;  /* Custom padding */
        font-size: revert;     /* Custom font size */
    }
`;

function GwTariffSection({ gatewayId }) {

    const [tariffInfo, setTariffInfo] = useState(null);
    const [tariffView, setTariffView] = useState(null);

    const [loading, setLoading] = useState(true);
    
    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
 
    const axiosInstance = useAxiosPrivate();

    const metersTariffView = [
        { key: 'asn', label: 'Tariff ASN' },
        { key: 'description', label: 'Tariff description', render: (item) => renderTariffDescription(item.asn) },
        { key: 'mantainanceReadouts', label: 'Meter maintenance readouts',  render: (item) => `${item.usedDMMaintenanceReadoutsPerYear} (${item.dmMaintenanceReadoutsPerYear} per year)` },
        { key: 'meterScans', label: 'Meter scans',  render: (item) => `${item.usedDMScansPerYear} (${item.dmScansPerYear} per year)` },
        { key: 'dmMaxNumberOfDevices', label: 'Supported devices'},
        { key: 'decryptionSupport', label: 'Decryption', render: (item) => item.decryptionSupport ? "Enabled" : "Disabled" },
    ];

    const networkTariffView = [
        { key: 'asn', label: 'Tariff ASN' },
        { key: 'description', label: 'Tariff description', render: (item) => renderTariffDescription(item.asn) },
        { key: 'mantainanceReadouts', label: 'Network maintenance readouts',  render: (item) => `${item.usedNetworkMaintenanceReadoutsPerYear} (${item.networkMaintenanceReadoutsPerYear} per year)` },
        { key: 'networkScans', label: 'Network scans',  render: (item) => `${item.usedNetworkScansPerYear} (${item.networkScansPerYear} per year)` },
        { key: 'modeChanges', label: 'Network mode changes',  render: (item) => `${item.networkChangeModesPerYear} (per year per network)` },
        { key: 'networkMaxNumberOfNetworks', label: 'Supported Q AMR networks' },
        { key: 'networkMaxNumberOfDevices', label: 'Supported devices', render: (item) => `${item.networkMaxNumberOfDevices} (per network)`},
        { key: 'decryptionSupport', label: 'Decryption', render: (item) => item.decryptionSupport ? "Enabled" : "Disabled" },
    ];

    const renderTariffDescription = (asn) => {
        switch(asn){
            case "QGW-D-012-0000":
                return "Direct metering (Readout once a month)";
            case "QGW-E-024-1000":
                return "Direct metering (Readout twice a month)";
            case "QGW-A-012-0000":
                return "Network (Readout once a month)";
            case "QGW-A-024-0000":
                return "Network (Readout twice a month)";
            default:
                return "Unknown Tariff";
        }
    };

    useEffect(() => {
        const fetchTariffInfo = async () => {
            try {
                const response = await axiosInstance.get(`/gateways/${gatewayId}/tariff`); // Fetch locations from API
                const data = response.data;
                setTariffInfo(data); // Store the locations in state
                clearError('fetchTariff');
                

                    // Set the correct tariff view
                if (data.dmSupport && !data.networkSupport) {
                    setTariffView(metersTariffView);
                } else if (!data.dmSupport && data.networkSupport) {
                    setTariffView(networkTariffView);
                } else {
                    // Both true or both false => Show error
                    setTariffView(null);
                    setError("fetchTariff", "Invalid tariff configuration. Cannot determine tariff type.");
                }

                setLoading(false);

            } catch (error) {
                setError('fetchTariff', error);
                showNotification('Error fetching locations!', 'error');
                setLoading(false);
            }
        };

        fetchTariffInfo();
    }, [gatewayId]);

    if (getError('fetchTariff')) return <Typography variant="h7">{getError('fetchTariff')}</Typography>;
    
    return ( 
        <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h7">TARIFF INFORMATIONS</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
                    { !loading && 
                    (<GenericDetailsTable
                    detailsValue={tariffInfo}
                    detailsList={tariffView} /> )}      
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

export default GwTariffSection;
